import { twMerge } from "tailwind-merge";
import {
  CatalogLink,
  CatalogAutoplayButton,
  CatalogPagingButton,
} from "catalog/CatalogNavigations";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Image, Anime, useLang } from "./elements";
import { useCatalog } from "catalog/Catalog.context";
import { AnimatePresence } from "framer-motion";

export default function GlobalNavigation() {
  const { activePageInfo } = useCatalog();

  if (!activePageInfo) return null;

  const { logo, gnb, bg } = activePageInfo.header || {};

  return (
    <div className="pointer-events-none absolute left-0 top-0 z-[9999] h-full w-full">
      <header
        className={twMerge(
          "pointer-events-auto h-auto w-full bg-white transition-colors duration-300",
          "mo:fixed mo:left-0 mo:top-0 mo:z-[9999] mo:border-b mo:!bg-white mo:shadow-md",
          bg === "transparent" && "bg-transparent",
        )}
      >
        <div
          className={twMerge(
            "relative grid h-[80px] w-full grid-cols-[250px_1fr_250px] items-center justify-center gap-[30px]",
            "mo:flex mo:h-[50px] mo:justify-between mo:px-[10px]",
          )}
        >
          <Anime
            initial={{ translateY: "-200px" }}
            animate={{ translateY: logo ? "0px" : "-200px" }}
            className={twMerge("ml-[37px]", "mo:ml-0 mo:flex-shrink-0 mo:!translate-y-0")}
          >
            <Logo />
          </Anime>
          <Anime
            initial={{ translateY: "-200px" }}
            animate={{ translateY: gnb ? "0px" : "-200px" }}
            className={twMerge("h-full w-full", "mo:hidden")}
          >
            <Gnb />
          </Anime>
          <Controls />
        </div>
      </header>
      <PagingButtons />
    </div>
  );
}

function Logo() {
  return (
    <CatalogLink to="cover/scene1">
      <Image src="common/logo-color.svg" className={twMerge("h-auto w-[136px]", "mo:w-[90px]")} />
    </CatalogLink>
  );
}

function Gnb() {
  const { pageSlug1, pageSlug2, pageSlug3, lang } = useParams();

  const gnbMap = [
    {
      name: useLang({ ko: "회사소개", en: "Company" }),
      to: "/company/overview",
      slug: "company",
      childs: [
        {
          name: useLang({ ko: "회사소개", en: "Overview" }),
          to: "/company/overview",
          slug: "overview",
        },
        {
          name: useLang({ ko: "기업연혁", en: "History" }),
          to: "/company/history",
          slug: "history",
        },
        {
          name: useLang({ ko: "기술력", en: "Technology" }),
          to: "/company/technology/1",
          slug: "technology",
        },
      ],
    },
    {
      name: useLang({ ko: "솔루션", en: "Solutions" }),
      to: "/solution/formwork/overview",
      slug: "solution",
      childs: [
        {
          name: "Formwork Solutions",
          to: "/solution/formwork/overview",
          slug: "formwork",
          width: useLang({ ko: 641, en: 680 }),
          childs: [
            {
              name: useLang({ ko: "개요", en: "Overview" }),
              to: "/solution/formwork/overview",
              slug: "overview",
            },
            {
              name: useLang({ ko: "특징", en: "Features" }),
              to: "/solution/formwork/features/1",
              slug: "features",
            },
            {
              name: useLang({ ko: "적용효과", en: "Application Effect" }),
              to: "/solution/formwork/application-effect",
              slug: "application-effect",
            },
            {
              name: useLang({ ko: "공법 비교", en: "Comparison" }),
              to: "/solution/formwork/compares/1",
              slug: "compares",
            },
            {
              name: useLang({ ko: "자재", en: "Components" }),
              to: "/solution/formwork/item",
              slug: "item",
            },
          ],
        },
        {
          name: useLang({ ko: "테이블폼 공법", en: "Table Form Solution" }),
          to: "solution/tableform-method/intro",
          slug: "tableform-method",
          width: useLang({ ko: 612, en: 622 }),
          childs: [
            {
              name: useLang({ ko: "공법 소개", en: "Overview" }),
              to: "solution/tableform-method/overview",
              slug: "overview",
            },
            {
              name: useLang({ ko: "설치 순서", en: "Installation Process" }),
              to: "solution/tableform-method/install-process",
              slug: "install-process",
            },
            {
              name: useLang({ ko: "적용 구간", en: "Applied Structures" }),
              to: "solution/tableform-method/applications/rc-beam",
              slug: "applications",
            },
            {
              name: useLang({ ko: "공법 과정", en: "Work Process" }),
              to: "solution/tableform-method/work-process",
              slug: "work-process",
            },
          ],
        },
        {
          name: useLang({ ko: "MS공법", en: "MS Solution" }),
          to: "solution/ms-method/intro",
          slug: "ms-method",
          width: useLang({ ko: 612, en: 622 }),
          childs: [
            {
              name: useLang({ ko: "공법 소개", en: "Overview" }),
              to: "solution/ms-method/overview",
              slug: "overview",
            },
            {
              name: useLang({ ko: "설치 순서", en: "Installation Process" }),
              to: "solution/ms-method/install-process",
              slug: "install-process",
            },
            {
              name: useLang({ ko: "적용 구간", en: "Applied Structures" }),
              to: "solution/ms-method/applications/pit",
              slug: "applications",
            },
            {
              name: useLang({ ko: "공법 과정", en: "Work Process" }),
              to: "solution/ms-method/work-process",
              slug: "work-process",
            },
          ],
        },
      ],
    },
    {
      name: useLang({ ko: "프로젝트", en: "Projects" }),
      to: "/projects/apt/1",
      slug: "projects",
      childs: [
        { name: useLang({ ko: "아파트", en: "Apartment" }), to: "/projects/apt/1", slug: "apt" },
        {
          name: useLang({ ko: "지식산업센터", en: "Knowledge Industry Center" }),
          to: "/projects/industrial-center/1",
          slug: "industrial-center",
        },
        {
          name: useLang({ ko: "데이터센터", en: "Data Center" }),
          to: "/projects/data-center/1",
          slug: "data-center",
        },
        {
          name: useLang({ ko: "복합건축물", en: "Complex Building" }),
          to: "/projects/building/1",
          slug: "building",
        },
      ],
    },
  ];

  return (
    <ul className={twMerge("flex h-full w-full items-center justify-center")}>
      {gnbMap.map((item) => (
        <li key={item.name}>
          <CatalogLink
            className={twMerge(
              "px-[2em] py-[0.8em] text-[22px] capitalize",
              "hover:text-dd-red",
              pageSlug1 === item.slug && "font-bold",
            )}
            to={item.to}
          >
            {item.name}
          </CatalogLink>
          {item.childs && pageSlug1 === item.slug && (
            <ul
              className={twMerge(
                "absolute left-[-280px] top-full h-[72px] w-[calc(100%+560px)] bg-dd-dark",
                "flex items-center justify-center",
              )}
            >
              {item.childs.map((child) => (
                <li
                  key={`gnb-sub-${item.name}-${child.name}`}
                  className={twMerge("group flex flex-shrink-0 items-center")}
                >
                  <CatalogLink
                    className={twMerge(
                      "relative flex-shrink-0 px-[3em] py-[0.3em] text-center text-[23px] font-normal capitalize text-[#C5C5C5]",
                      "hover:text-white",
                      "after:absolute after:right-0 after:top-1/2 after:h-[0.9em] after:w-[2px] after:-translate-x-1/2 after:-translate-y-1/2 after:bg-[#474645] group-last:after:hidden",
                      pageSlug2 === child.slug && "font-bold text-white",
                      child.childs &&
                        "mx-3 w-[232px] rounded-full bg-[#474645] px-0 py-[0.3em] after:hidden",
                      pageSlug2 === child.slug && child.childs && "bg-dd-red",
                      lang === "en" && child.childs && "text-xl",
                    )}
                    to={child.to}
                  >
                    {child.name}
                  </CatalogLink>
                  <AnimatePresence>
                    {child.childs && pageSlug2 === child.slug && (
                      <Anime
                        initial={{ width: 0 }}
                        animate={{ width: child.width }}
                        exit={{ width: 0 }}
                        className={twMerge(
                          "flex flex-shrink-0 flex-nowrap items-center overflow-hidden",
                        )}
                      >
                        {child.childs.map((subChild) => (
                          <CatalogLink
                            key={`gnb-sub-${item.name}-${child.name}-${subChild.name}`}
                            className={twMerge(
                              "relative flex-shrink-0 px-[1.5em] py-[0.3em] text-[23px] font-normal capitalize text-[#C5C5C5]",
                              "hover:text-white",
                              "after:absolute after:right-0 after:top-1/2 after:h-[0.9em] after:w-[2px] after:-translate-x-1/2 after:-translate-y-1/2 after:bg-[#474645] last:after:hidden",
                              pageSlug3 === subChild.slug && "font-bold text-white",
                              lang === "en" && "text-base",
                            )}
                            to={subChild.to}
                          >
                            {subChild.name}
                          </CatalogLink>
                        ))}
                      </Anime>
                    )}
                  </AnimatePresence>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

function Controls() {
  const classNames = {
    container: twMerge("flex h-full items-center", "mo:gap-[5px] mo:items-center"),
    button: twMerge(
      "h-full",
      "hover:opacity-60",
      "mo:relative mo:h-[40px] mo:w-[40px] mo:bg-[#f9f8f7] rounded-lg",
    ),
    icon: twMerge(
      "w-[60px] h-full object-cover",
      "mo:h-[120%] mo:w-auto mo:absolute mo:top-1/2 mo:left-1/2 mo:-translate-x-1/2 mo:-translate-y-1/2 mo:max-w-none",
    ),
  };

  return (
    <nav className={classNames.container}>
      <LangSwitcher />
      <CatalogAutoplayButton
        className={classNames.button}
        render={{
          play: <Image src="common/icon-play.svg" className={classNames.icon} />,
          pause: <Image src="common/icon-pause.svg" className={classNames.icon} />,
        }}
      />
      <CatalogLink to="cover/catalog-index" className={classNames.button}>
        <Image src="common/icon-hambuger.svg" className={classNames.icon} />
      </CatalogLink>
    </nav>
  );
}

function PagingButtons() {
  const { isFirstPage, isLastPage } = useCatalog();

  return (
    <nav
      className={twMerge(
        "absolute bottom-0 left-0 flex w-full justify-between *:pointer-events-auto",
        "mo:fixed mo:z-[9999] mo:border-t mo:bg-white",
      )}
    >
      <CatalogPagingButton
        direction="prev"
        className={twMerge(
          "group flex h-[80px] w-[80px] items-center justify-center",
          isFirstPage && "pointer-events-none opacity-0",
          "mo:h-[50px] mo:w-[50px] mo:p-[5px]",
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28.32"
          height="25.076"
          viewBox="0 0 28.32 25.076"
          className={twMerge("mo:h-[60%] mo:w-[60%] mo:object-cover")}
        >
          <path
            d="M15.732,0V15.732H0"
            transform="translate(25.076 12.538) rotate(135)"
            fill="none"
            stroke="#9e9e9e"
            strokeMiterlimit="10"
            strokeWidth="4"
            className={twMerge("group-hover:stroke-dd-red")}
          />
          <line
            x1="25.275"
            transform="translate(3.045 12.326)"
            fill="none"
            stroke="#9e9e9e"
            strokeWidth="4"
            className={twMerge("group-hover:stroke-dd-red")}
          />
        </svg>
      </CatalogPagingButton>
      <CatalogPagingButton
        direction="next"
        className={twMerge(
          "group flex h-[80px] w-[80px] items-center justify-center",
          isLastPage && "pointer-events-none opacity-0",
          "mo:h-[50px] mo:w-[50px] mo:p-[5px]",
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28.319"
          height="25.076"
          viewBox="0 0 28.319 25.076"
          className={twMerge("mo:h-[60%] mo:w-[60%] mo:object-cover")}
        >
          <path
            d="M15.731,15.732V0H0"
            transform="translate(14.367 1.414) rotate(45)"
            fill="none"
            stroke="#9e9e9e"
            strokeMiterlimit="10"
            strokeWidth="4"
            className={twMerge("group-hover:stroke-dd-red")}
          />
          <line
            x2="25.275"
            transform="translate(0 12.326)"
            fill="none"
            stroke="#9e9e9e"
            strokeWidth="4"
            className={twMerge("group-hover:stroke-dd-red")}
          />
        </svg>
      </CatalogPagingButton>
    </nav>
  );
}

function LangSwitcher() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const classNames = {
    container: twMerge(
      "flex items-center gap-[10px] mr-2 rounded-full bg-[#f2f1f0] p-[10px] mo:p-[5px] mo:gap-[5px]",
    ),
    button: twMerge("relative rounded-full flex items-center justify-center opacity-50"),
    icon: twMerge("w-[30px]", "mo:w-[24px]"),
  };

  const handleLang = (newLang) => {
    const newPath = location.pathname.replace(`/${lang}/`, `/${newLang}/`);
    navigate(newPath);
  };

  return (
    <div className={classNames.container}>
      <button
        onClick={() => handleLang("ko")}
        className={twMerge(classNames.button, lang === "ko" && "opacity-100")}
      >
        <Image src="common/lang-ko.png" alt="" className={classNames.icon} />
      </button>
      <button
        onClick={() => handleLang("en")}
        className={twMerge(classNames.button, lang === "en" && "opacity-100")}
      >
        <Image src="common/lang-en.png" alt="" className={classNames.icon} />
      </button>
    </div>
  );
}
